import {
  Children,
  cloneElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Cell,
  Column,
  Pagination,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
  type SortDescriptor,
} from '@skupreme/shared/ui';
import {
  divisionLabelOf,
  hasDivision,
  useActiveScope,
} from '@features/account-view/active';
import type { ISortObject, ITableColumn } from '../../legacy-bridge';
import { ColumnHeader } from './ColumnHeader';
import { DivisionLabel } from './DivisionLabel';
import {
  OrderItemsThumbCell,
  type OrderLineImages,
} from './OrderItemsThumbCell';
import {
  columnWidthStyle,
  fromSortDescriptor,
  toSortDescriptor,
} from '../../lib/table';

export type OrdersTableProps<T> = {
  columns: ITableColumn[];
  currentPage?: number;
  data: T[];
  loading?: boolean;
  loadingRows?: number;
  noDataMessage?: ReactNode;
  pageChanged?: (page: number) => void;
  pageSize?: number;
  pageSizeChanged?: (size: number) => void;
  renderRow: (item: T) => ReactElement;
  sort?: ISortObject;
  sortChanged?: (sort: ISortObject, key: string) => void;
  totalData?: number;
  /** Unused: the pager derives it. Kept for call sites that still pass it. */
  totalPages?: number;
  wrapperClass?: string;
};

const ALIGNMENT: Record<'left' | 'center' | 'right', string> = {
  left: 'sk:text-left',
  center: 'sk:text-center',
  right: 'sk:text-right',
};

/* EN-4396: not sortable, the API does not sort by division. */
const DIVISION_COLUMN: ITableColumn = {
  text: 'Division',
  key: 'division',
  textAlign: 'left',
  width: 2,
  component: <ColumnHeader label="Division" />,
};

/* Next to the order number, which is the first column. */
const withDivisionColumn = (columns: ITableColumn[]) => [
  columns[0],
  DIVISION_COLUMN,
  ...columns.slice(1),
];

/* Before the order number; one grid unit, taken from the order number's column. */
const THUMB_WIDTH = 1;

const THUMB_COLUMN: ITableColumn = {
  text: 'Picture',
  key: 'item_thumbs',
  textAlign: 'left',
  width: THUMB_WIDTH,
  component: <span className="sk:sr-only">Picture</span>,
};

const withThumbColumn = (columns: ITableColumn[]) => {
  const [first, ...rest] = columns;
  const width =
    typeof first.width === 'number' && first.width > 1
      ? ((first.width - THUMB_WIDTH) as ITableColumn['width'])
      : first.width;

  return [THUMB_COLUMN, { ...first, width }, ...rest];
};

const hasItemLines = (row: unknown): row is { items: OrderLineImages[] } =>
  typeof row === 'object' &&
  row !== null &&
  'items' in row &&
  Array.isArray(row.items);

/*
 * The caller's row is a component of its own that renders the `<Row>` and its
 * cells. Rendering it here, rather than as an element, lets the picture cell be
 * put in before the first one and the division cell after it, without the
 * caller knowing about either.
 */
function ExtraCellsRow({
  row,
  before,
  afterFirst,
}: {
  row: ReactElement;
  before: ReactNode;
  afterFirst: ReactNode;
}) {
  if (typeof row.type !== 'function') {
    return row;
  }

  const rendered = (row.type as (props: unknown) => ReactElement)(row.props);
  const [first, ...rest] = Children.toArray(
    (rendered.props as { children?: ReactNode }).children,
  );

  return cloneElement(rendered, undefined, before, first, afterFirst, ...rest);
}

export function OrdersTable<T>({
  columns,
  currentPage,
  data,
  loading,
  loadingRows = 4,
  noDataMessage = 'No Data Found',
  pageChanged,
  pageSize,
  pageSizeChanged,
  renderRow,
  sort,
  sortChanged,
  totalData,
  wrapperClass,
}: OrdersTableProps<T>) {
  /* Only the account level's rows carry a division, the held list's do not. */
  const { level } = useActiveScope();
  const showDivision = level === 'account' && data.every(hasDivision);
  const showThumbs = data.every(hasItemLines);
  const withDivision = showDivision ? withDivisionColumn(columns) : columns;
  const shownColumns = showThumbs
    ? withThumbColumn(withDivision)
    : withDivision;

  const onSortChange = (descriptor: SortDescriptor) => {
    const request = fromSortDescriptor(columns, descriptor);

    if (request) {
      sortChanged?.(request.sort, request.key);
    }
  };

  const showPagination = (currentPage || 0) > 0 && (totalData || 0) > 0;

  return (
    <>
      {/* Overflow stays visible: an `overflow-auto` container would become the
          scrollport and pin the sticky header to a box that never scrolls. */}
      <TableContainer
        className={`sk:mt-4 sk:overflow-visible sk:rounded-card sk:bg-surface ${wrapperClass || ''}`}
      >
        <Table
          aria-label="Orders"
          className="sk:table-fixed sk:text-dense"
          sortDescriptor={toSortDescriptor(shownColumns, sort)}
          onSortChange={onSortChange}
        >
          <TableHeader>
            {shownColumns.map(column => (
              <Column
                key={column.key}
                id={column.key}
                isRowHeader={column.key === columns[0]?.key}
                allowsSorting={!!column.sortKey && !column.hideSort}
                className={`sk:text-xs ${ALIGNMENT[column.textAlign || 'left']}`}
                style={{ ...column.style, ...columnWidthStyle(column.width) }}
              >
                {column.component || column.text}
              </Column>
            ))}
          </TableHeader>

          <TableBody
            renderEmptyState={() => (
              <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
                {noDataMessage}
              </div>
            )}
          >
            {loading
              ? Array.from({ length: data.length || loadingRows }, (_, row) => (
                  <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
                    {shownColumns.map(column => (
                      <Cell key={column.key}>
                        <div className="sk:h-4 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
                      </Cell>
                    ))}
                  </Row>
                ))
              : data.map(item => {
                  const row = renderRow(item);

                  const thumb =
                    showThumbs && hasItemLines(item) ? (
                      <Cell key="item_thumbs">
                        <OrderItemsThumbCell items={item.items} />
                      </Cell>
                    ) : null;
                  const division =
                    showDivision && hasDivision(item) ? (
                      <Cell key="division">
                        <DivisionLabel
                          label={divisionLabelOf(item)}
                          division={{
                            id: item.division_id,
                            name: item.division_name,
                          }}
                        />
                      </Cell>
                    ) : null;

                  return thumb || division ? (
                    <ExtraCellsRow
                      key={row.key}
                      row={row}
                      before={thumb}
                      afterFirst={division}
                    />
                  ) : (
                    row
                  );
                })}
          </TableBody>
        </Table>

        {showPagination && (
          <Pagination
            page={currentPage ?? 1}
            pageSize={pageSize ?? 10}
            total={totalData ?? 0}
            onPageChange={pageChanged ?? (() => {})}
            onPageSizeChange={pageSizeChanged ?? (() => {})}
          />
        )}
      </TableContainer>
    </>
  );
}
